// src/parsers/gerberToSvg.ts
// Convert RS-274X Gerber files to SVG paths

import type { ConfirmedGerberLayer } from './gerberDetection';

interface GerberState {
  x: number;
  y: number;
  i: number;
  j: number;
  dCode: number;
  interpolationMode: 'linear' | 'circularCW' | 'circularCCW';
  quadrantMode: 'single' | 'multi';
  coordinateFormat: {
    integer: number;
    decimal: number;
    omitLeadingZeros: boolean;
  } | null;
  units: 'in' | 'mm' | null;
  apertures: Map<number, { type: string; params: number[]; hole?: number }>;
  currentAperture: number | null;
  isInRegion: boolean;
  regionPoints: Array<{ x: number; y: number }>;
}

interface ParsedLine {
  x?: number;
  y?: number;
  i?: number;
  j?: number;
  dCode?: number;
  gCode?: number;
}

function parseCoordinate(value: string, format: { integer: number; decimal: number; omitLeadingZeros: boolean }): number {
  const cleanValue = value.replace(/^[+-]/, '');
  const sign = value.startsWith('-') ? -1 : 1;
  
  if (format.omitLeadingZeros) {
    const totalDigits = format.integer + format.decimal;
    const padded = cleanValue.padStart(totalDigits, '0');
    const intPart = padded.slice(0, format.integer) || '0';
    const decPart = padded.slice(format.integer);
    return sign * parseFloat(`${intPart}.${decPart}`);
  } else {
    const totalDigits = format.integer + format.decimal;
    const padded = cleanValue.padEnd(totalDigits, '0');
    const intPart = padded.slice(0, format.integer) || '0';
    const decPart = padded.slice(format.integer);
    return sign * parseFloat(`${intPart}.${decPart}`);
  }
}

function parseGerberLine(line: string, state: GerberState): ParsedLine {
  const result: ParsedLine = {};
  
  const xMatch = line.match(/X([+-]?\d+)/);
  if (xMatch && state.coordinateFormat) {
    result.x = parseCoordinate(xMatch[1], state.coordinateFormat);
  }
  
  const yMatch = line.match(/Y([+-]?\d+)/);
  if (yMatch && state.coordinateFormat) {
    result.y = parseCoordinate(yMatch[1], state.coordinateFormat);
  }
  
  const iMatch = line.match(/I([+-]?\d+)/);
  if (iMatch && state.coordinateFormat) {
    result.i = parseCoordinate(iMatch[1], state.coordinateFormat);
  }
  
  const jMatch = line.match(/J([+-]?\d+)/);
  if (jMatch && state.coordinateFormat) {
    result.j = parseCoordinate(jMatch[1], state.coordinateFormat);
  }
  
  const dMatch = line.match(/D0?(\d+)/);
  if (dMatch) {
    result.dCode = parseInt(dMatch[1], 10);
  }
  
  const gMatch = line.match(/G0?(\d+)/);
  if (gMatch) {
    result.gCode = parseInt(gMatch[1], 10);
  }
  
  return result;
}

function calculateArc(
  startX: number, startY: number,
  endX: number, endY: number,
  i: number, j: number,
  isCW: boolean,
  isMultiQuadrant: boolean
): string {
  const centerX = startX + i;
  const centerY = startY + j;
  const radius = Math.sqrt(i * i + j * j);
  
  let startAngle = Math.atan2(startY - centerY, startX - centerX) * 180 / Math.PI;
  let endAngle = Math.atan2(endY - centerY, endX - centerX) * 180 / Math.PI;
  
  if (isCW) {
    while (endAngle > startAngle) {
      endAngle -= 360;
    }
  } else {
    while (endAngle < startAngle) {
      endAngle += 360;
    }
  }
  
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  const sweep = isCW ? 0 : 1;
  
  return `A ${radius} ${radius} 0 ${largeArc} ${sweep} ${endX} ${endY}`;
}

function processExtendedCommand(lines: string[], startIndex: number, state: GerberState): number {
  const line = lines[startIndex].trim();
  
  const formatMatch = line.match(/%FS(L|T)(A|I)X(\d)(\d)Y(\d)(\d)/);
  if (formatMatch) {
    state.coordinateFormat = {
      omitLeadingZeros: formatMatch[1] === 'L',
      integer: parseInt(formatMatch[3], 10),
      decimal: parseInt(formatMatch[4], 10)
    };
    return startIndex + 1;
  }
  
  if (line.includes('MOIN')) {
    state.units = 'in';
    return startIndex + 1;
  }
  if (line.includes('MOMM')) {
    state.units = 'mm';
    return startIndex + 1;
  }
  
  const apertureMatch = line.match(/%ADD(\d+)([A-Z])([^%]*)%?/);
  if (apertureMatch) {
    const dCode = parseInt(apertureMatch[1], 10);
    const type = apertureMatch[2];
    const params = apertureMatch[3]
      .replace(/\*$/, '')
      .split(/X/i)
      .map(v => parseFloat(v.trim()))
      .filter(v => !isNaN(v));
    
    state.apertures.set(dCode, { type, params });
    return startIndex + 1;
  }
  
  if (line.startsWith('%AM')) {
    let i = startIndex;
    while (i < lines.length && !lines[i].includes('%')) {
      i++;
    }
    return i + 1;
  }
  
  return startIndex + 1;
}

interface ApertureDef {
  number: number;
  type: string;
  diameter: number;
  width?: number;
  height?: number;
  innerDiameter?: number;
}

function gerberToPaths(content: string): { 
  paths: string[]; 
  regionPaths: string[];
  flashes: Array<{ x: number; y: number; aperture: number }>;
  apertures: Map<number, ApertureDef>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number } 
} {
  const state: GerberState = {
    x: 0,
    y: 0,
    i: 0,
    j: 0,
    dCode: 2,
    interpolationMode: 'linear',
    quadrantMode: 'multi',
    coordinateFormat: null,
    units: null,
    apertures: new Map(),
    currentAperture: null,
    isInRegion: false,
    regionPoints: []
  };
  
  const paths: string[] = [];
  const regionPaths: string[] = [];
  const flashes: Array<{ x: number; y: number; aperture: number }> = [];
  
  let currentPath = '';
  let lastX = 0, lastY = 0;
  
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  
  const lines = content.split(/\r?\n/).map(l => l.trim());
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    
    if (!line) {
      i++;
      continue;
    }
    
    if (line.startsWith('G04')) {
      i++;
      continue;
    }
    
    if (line.startsWith('%')) {
      i = processExtendedCommand(lines, i, state);
      continue;
    }
    
    if (line.includes('M02')) {
      break;
    }
    
    const commands = line.split('*').filter(cmd => cmd.trim());
    
    for (const cmd of commands) {
      const parsed = parseGerberLine(cmd, state);
      
      if (parsed.gCode !== undefined) {
        switch (parsed.gCode) {
          case 1:
            state.interpolationMode = 'linear';
            break;
          case 2:
            state.interpolationMode = 'circularCW';
            break;
          case 3:
            state.interpolationMode = 'circularCCW';
            break;
          case 36:
            if (currentPath) {
              paths.push(currentPath);
              currentPath = '';
            }
            state.isInRegion = true;
            state.regionPoints = [];
            break;
          case 37:
            state.isInRegion = false;
            if (state.regionPoints.length > 2) {
              const regionPath = 'M ' + state.regionPoints.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z';
              regionPaths.push(regionPath);
            }
            state.regionPoints = [];
            break;
          case 74:
            state.quadrantMode = 'single';
            break;
          case 75:
            state.quadrantMode = 'multi';
            break;
        }
      }
      
      if (parsed.x !== undefined) state.x = parsed.x;
      if (parsed.y !== undefined) state.y = parsed.y;
      if (parsed.i !== undefined) state.i = parsed.i;
      if (parsed.j !== undefined) state.j = parsed.j;
      
      if (parsed.dCode !== undefined) {
        state.dCode = parsed.dCode;
        
        if (parsed.dCode === 1) {
          if (currentPath === '') {
            currentPath = `M ${state.x} ${state.y}`;
          } else {
            if (state.interpolationMode === 'linear') {
              currentPath += ` L ${state.x} ${state.y}`;
            } else {
              const isCW = state.interpolationMode === 'circularCW';
              const arcCmd = calculateArc(
                lastX, lastY, state.x, state.y,
                state.i, state.j, isCW, state.quadrantMode === 'multi'
              );
              currentPath += ` ${arcCmd}`;
            }
          }
        } else if (parsed.dCode === 2) {
          if (currentPath) {
            paths.push(currentPath);
            currentPath = '';
          }
          currentPath = `M ${state.x} ${state.y}`;
        } else if (parsed.dCode === 3) {
          if (currentPath) {
            paths.push(currentPath);
            currentPath = '';
          }
          if (state.currentAperture) {
            flashes.push({ x: state.x, y: state.y, aperture: state.currentAperture });
          }
        } else if (parsed.dCode >= 10) {
          state.currentAperture = parsed.dCode;
        }
      }
      
      if (state.isInRegion && (parsed.x !== undefined || parsed.y !== undefined)) {
        state.regionPoints.push({ x: state.x, y: state.y });
      }
      
      if (parsed.x !== undefined) {
        minX = Math.min(minX, state.x);
        maxX = Math.max(maxX, state.x);
      }
      if (parsed.y !== undefined) {
        minY = Math.min(minY, state.y);
        maxY = Math.max(maxY, state.y);
      }
      
      lastX = state.x;
      lastY = state.y;
    }
    
    i++;
  }
  
  if (currentPath) {
    paths.push(currentPath);
  }
  
  if (minX === Infinity) minX = 0;
  if (minY === Infinity) minY = 0;
  if (maxX === -Infinity) maxX = 100;
  if (maxY === -Infinity) maxY = 100;
  
  // Convert internal aperture format to ApertureDef format
  const apertures: Map<number, ApertureDef> = new Map();
  for (const [dCode, apt] of state.apertures.entries()) {
    apertures.set(dCode, convertApertureToDef(dCode, apt.type, apt.params));
  }

  return { 
    paths, 
    regionPaths,
    flashes,
    apertures,
    bounds: { minX, minY, maxX, maxY } 
  };
}

function convertApertureToDef(
  dCode: number,
  type: string,
  params: number[]
): ApertureDef {
  const baseDef: ApertureDef = { number: dCode, type, diameter: 0.1 };
  
  switch (type) {
    case 'C': // Circle: C,outer_dia[,inner_dia[,cross_hair]]
      baseDef.diameter = params[0] || 0.1;
      if (params.length > 1) baseDef.innerDiameter = params[1];
      break;
    
    case 'R': // Rectangle: R,width,height[,hole_dia]
      baseDef.width = params[0] || 0.1;
      baseDef.height = params[1] || params[0] || 0.1;
      baseDef.diameter = Math.max(baseDef.width, baseDef.height);
      break;
    
    case 'O': // Obround: O,width,height[,hole_dia]
      baseDef.width = params[0] || 0.1;
      baseDef.height = params[1] || params[0] || 0.1;
      baseDef.diameter = Math.max(baseDef.width, baseDef.height);
      break;
    
    case 'P': // Polygon: P,outer_dia,vertices[,rotation[,hole_dia]]
      baseDef.diameter = params[0] || 0.1;
      break;
    
    default:
      baseDef.diameter = params[0] || 0.1;
  }
  
  return baseDef;
}

export function parseGerberToSvg(layer: ConfirmedGerberLayer): {
  svgContent: string;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
} {
  try {
    const { paths, regionPaths, flashes, apertures, bounds } = gerberToPaths(layer.content);
    
    const color = getLayerColor(layer.role);
    const opacity = getLayerOpacity(layer.role);
    
    // Build SVG content as a group with proper styling
    let svgContent = `<g class="layer-${layer.role}" data-side="${layer.side}" opacity="${opacity}">`;
    
    // Add region fills first (they should be behind strokes)
    for (const regionPath of regionPaths) {
      svgContent += `  <path d="${regionPath}" fill="${color}" stroke="none" />\n`;
    }
    
    // Add strokes for paths
    for (const path of paths) {
      if (path && path.length > 10) { // Skip very short/empty paths
        svgContent += `  <path d="${path}" fill="none" stroke="${color}" stroke-width="0.05" stroke-linecap="round" stroke-linejoin="round" />\n`;
      }
    }
    
    // Add flashes (aperture placements) with appropriate aperture shapes
    for (const flash of flashes) {
      const aperture = apertures.get(flash.aperture);
      if (aperture) {
        svgContent += renderApertureFlash(flash.x, flash.y, aperture, color);
      } else {
        // Default to small circle if aperture not found
        svgContent += `  <circle cx="${flash.x}" cy="${flash.y}" r="0.1" fill="${color}" />\n`;
      }
    }
    
    svgContent += '</g>';
    
    return { svgContent, bounds };
  } catch (error) {
    console.error('Error parsing Gerber:', error);
    return {
      svgContent: '<g></g>',
      bounds: { minX: 0, minY: 0, maxX: 100, maxY: 100 }
    };
  }
}

export function parseBoardOutline(content: string): Array<{ x: number; y: number }> {
  const { paths, bounds } = gerberToPaths(content);
  const outline: Array<{ x: number; y: number }> = [];
  
  for (const path of paths) {
    const matches = path.matchAll(/[ML]\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g);
    for (const match of matches) {
      const x = parseFloat(match[1]);
      const y = parseFloat(match[2]);
      if (!isNaN(x) && !isNaN(y)) {
        outline.push({ x, y });
      }
    }
  }
  
  return outline.length > 0 ? outline : [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY }
  ];
}

function renderApertureFlash(
  x: number, 
  y: number, 
  aperture: { number: number; type: string; diameter: number; width?: number; height?: number; innerDiameter?: number },
  color: string
): string {
  const { type, diameter, width, height, innerDiameter } = aperture;
  
  switch (type) {
    case 'C': // Circle aperture
      return `  <circle cx="${x}" cy="${y}" r="${diameter / 2}" fill="${color}" />\n`;
    
    case 'R': // Rectangle aperture
      const w = width || diameter;
      const h = height || diameter;
      return `  <rect x="${x - w/2}" y="${y - h/2}" width="${w}" height="${h}" fill="${color}" />\n`;
    
    case 'O': // Obround (oval)
      const ow = width || diameter;
      const oh = height || diameter;
      const or = Math.min(ow, oh) / 2;
      return `  <ellipse cx="${x}" cy="${y}" rx="${ow/2}" ry="${oh/2}" fill="${color}" />\n`;
    
    case 'P': // Polygon aperture
      // Simplified: render as circle for now
      return `  <circle cx="${x}" cy="${y}" r="${diameter / 2}" fill="${color}" />\n`;
    
    default:
      return `  <circle cx="${x}" cy="${y}" r="${diameter / 2}" fill="${color}" />\n`;
  }
}

export function getLayerColor(role: string): string {
  const colors: Record<string, string> = {
    copper: '#d4a574',
    soldermask: '#1a5f1a',
    silkscreen: '#ffffff',
    paste: '#808080',
    outline: '#ff0000',
    drill: '#00ffff',
    other: '#cccccc'
  };
  
  return colors[role] || colors.other;
}

export function getLayerOpacity(role: string): number {
  const opacities: Record<string, number> = {
    copper: 1.0,
    soldermask: 0.8,
    silkscreen: 1.0,
    paste: 0.9,
    outline: 1.0,
    drill: 0.5,
    other: 0.7
  };
  
  return opacities[role] ?? 0.7;
}

export function formatLayerName(layer: ConfirmedGerberLayer): string {
  const side = layer.side === 'top' ? 'Top' : layer.side === 'bottom' ? 'Bottom' : 'Inner';
  const role = layer.role.charAt(0).toUpperCase() + layer.role.slice(1);
  return `${side} ${role}`;
}